import { useQuery } from '@apollo/client'
import { Clear } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    TablePagination,
    Typography,
} from '@mui/material'
import {
    GridFooterContainer,
    GridPreferencePanelsValue,
    useGridApiContext,
} from '@mui/x-data-grid-pro'
import { GetCompaniesById } from 'candidate/graphql/generatedProfile.gql'
import { type FC, type ReactNode } from 'react'
import {
    GetFundSizeBucketOptions,
    GetIndustryOptions,
    GetSectorOptions,
    GetVisaStatusOptions,
} from 'recruiter/graphql/companies.gql'
import { useRosterSearch, useRosterSqlSearch } from 'recruiter/hooks'
import {
    DEFAULT_STATUSES,
    type RosterFilterState,
} from 'shared/hooks/rosterFilterState'
import { startCase } from 'lodash'
import { GetOfficeLocationsById } from 'shared/components/OfficeLocationsAutocomplete/OfficeLocationsAutocomplete.gql'
import { GetRecruitersByUserIds } from 'shared/components/RecruitersAutocomplete/RecruitersAutocomplete.gql'
import { GetProcessStatuses } from 'shared/graphql/candidateProcessStatus.gql'
import { GetTitleOptions } from 'shared/graphql/titles.gql'
import { getFullName, getPxAsRem } from 'shared/utils'
import { RosterTableToolbar } from './RosterTableToolbar'
import { minimalTagSx } from './styles'

interface LookupData {
    companies: Array<{ id: string; value: string }>
    recruiters: Array<{
        id: string
        userId: string
        user: { firstName: string; lastName: string; nickname?: string | null }
    }>
    industries: Array<{ id: string; value: string }>
    sectors: Array<{ id: string; value: string }>
    locations: Array<{ id: string; value: string }>
    titles: Array<{ id: string; value: string }>
    fundSizeBuckets: Array<{ id: string; value: string }>
    visaStatuses: Array<{ id: string; value: string }>
    processStatuses: Array<{
        id: string
        statusLabel: string
        processStatusSummaryGroup?: { id: string; label: string } | null
    }>
}

const formatDateRange = (
    gte: string | undefined,
    lte: string | undefined
): string | null => {
    const formatDate = (iso: string | undefined): string => {
        if (!iso) return ''
        const d = new Date(iso)
        return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    }
    const from = formatDate(gte)
    const to = formatDate(lte)
    if (from && to) return `${from} - ${to}`
    if (from) return `From ${from}`
    if (to) return `Until ${to}`
    return null
}

interface FilterConfig {
    key: string
    label: string
    isActive: (f: RosterFilterState) => boolean
    getDisplayValue: (f: RosterFilterState, lookup: LookupData) => string | null
    getClearFields: () => Partial<RosterFilterState>
}

const FILTER_CONFIGS: FilterConfig[] = [
    {
        key: 'quickSearch',
        label: 'Search',
        isActive: (f) => !!f.quickSearch,
        getDisplayValue: (f) => f.quickSearch || null,
        getClearFields: () => ({ quickSearch: undefined }),
    },
    {
        key: 'companyIds',
        label: 'Company',
        isActive: (f) => f.companyIds.length > 0,
        getDisplayValue: (f, { companies }) => {
            const names = f.companyIds
                .map((id) => companies.find((c) => c.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ companyIds: [] }),
    },
    {
        key: 'createdByUserIds',
        label: 'Created By',
        isActive: (f) => f.createdByUserIds.length > 0,
        getDisplayValue: (f, { recruiters }) => {
            const names = f.createdByUserIds
                .map((userId) => {
                    const r = recruiters.find((r) => r.userId === userId)
                    return r ? getFullName(r.user) : null
                })
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ createdByUserIds: [] }),
    },
    {
        key: 'internalUserIds',
        label: 'Internal Users',
        isActive: (f) => f.internalUserIds.length > 0,
        getDisplayValue: (f, { recruiters }) => {
            const names = f.internalUserIds
                .map((userId) => {
                    const r = recruiters.find((r) => r.userId === userId)
                    return r ? getFullName(r.user) : null
                })
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ internalUserIds: [] }),
    },
    {
        key: 'industryIds',
        label: 'Industry',
        isActive: (f) => f.industryIds.length > 0,
        getDisplayValue: (f, { industries }) => {
            const names = f.industryIds
                .map((id) => industries.find((i) => i.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ industryIds: [] }),
    },
    {
        key: 'sectorIds',
        label: 'Sector',
        isActive: (f) => f.sectorIds.length > 0,
        getDisplayValue: (f, { sectors }) => {
            const names = f.sectorIds
                .map((id) => sectors.find((s) => s.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ sectorIds: [] }),
    },
    {
        key: 'geoLocationIds',
        label: 'Location',
        isActive: (f) => f.geoLocationIds.length > 0,
        getDisplayValue: (f, { locations }) => {
            const names = f.geoLocationIds
                .map((id) => locations.find((l) => l.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ geoLocationIds: [] }),
    },
    {
        key: 'geoLocationText',
        label: 'Location',
        isActive: (f) => f.geoLocationText.length > 0,
        getDisplayValue: (f) =>
            f.geoLocationText
                .map((v) => (v.startsWith(', ') ? v.slice(2) : v))
                .join(', '),
        getClearFields: () => ({ geoLocationText: [] }),
    },
    {
        key: 'titleIds',
        label: 'Title',
        isActive: (f) => f.titleIds.length > 0,
        getDisplayValue: (f, { titles }) => {
            const names = f.titleIds
                .map((id) => titles.find((t) => t.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ titleIds: [] }),
    },
    {
        key: 'consultants',
        label: 'Consultants',
        isActive: (f) => !!f.consultants,
        getDisplayValue: (f) =>
            f.consultants === 'yes'
                ? 'Yes'
                : f.consultants === 'no'
                  ? 'No'
                  : null,
        getClearFields: () => ({ consultants: undefined }),
    },
    {
        key: 'fundSizeIds',
        label: 'Fund Size',
        isActive: (f) => f.fundSizeIds.length > 0,
        getDisplayValue: (f, { fundSizeBuckets }) => {
            const names = f.fundSizeIds
                .map((id) => fundSizeBuckets.find((b) => b.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ fundSizeIds: [] }),
    },
    {
        key: 'visaStatusIds',
        label: 'Visa',
        isActive: (f) => f.visaStatusIds.length > 0,
        getDisplayValue: (f, { visaStatuses }) => {
            const names = f.visaStatusIds
                .map((id) => visaStatuses.find((v) => v.id === id)?.value)
                .filter(Boolean)
            return names.length > 0 ? names.join(', ') : null
        },
        getClearFields: () => ({ visaStatusIds: [] }),
    },
    {
        key: 'statuses',
        label: 'Status',
        isActive: (f) =>
            f.statuses.length !== DEFAULT_STATUSES.length ||
            !f.statuses.every((s) =>
                (DEFAULT_STATUSES as string[]).includes(s)
            ),
        getDisplayValue: (f) => f.statuses.map((s) => startCase(s)).join(', '),
        getClearFields: () => ({ statuses: DEFAULT_STATUSES }),
    },
    {
        key: 'type',
        label: 'Type',
        isActive: (f) => !!f.type,
        getDisplayValue: (f) => f.type || null,
        getClearFields: () => ({ type: undefined }),
    },
    {
        key: 'createdAt',
        label: 'Created',
        isActive: (f) => !!f.createdAtGte || !!f.createdAtLte,
        getDisplayValue: (f) => formatDateRange(f.createdAtGte, f.createdAtLte),
        getClearFields: () => ({
            createdAtGte: undefined,
            createdAtLte: undefined,
        }),
    },
    {
        key: 'startDates',
        label: 'Start Date',
        isActive: (f) => f.startDates.length > 0,
        getDisplayValue: (f) => f.startDates.join(', '),
        getClearFields: () => ({ startDates: [] }),
    },
    {
        key: 'processStatus',
        label: 'Process Status',
        isActive: (f) =>
            f.processStatusIds.length > 0 || f.processStatusGroupIds.length > 0,
        getDisplayValue: (f, { processStatuses }) => {
            const groupNames = f.processStatusGroupIds
                .map((groupId) => {
                    const ps = processStatuses.find(
                        (ps) => ps.processStatusSummaryGroup?.id === groupId
                    )
                    return ps?.processStatusSummaryGroup?.label
                })
                .filter(Boolean)
            const statusNames = f.processStatusIds
                .map(
                    (id) =>
                        processStatuses.find((ps) => ps.id === id)?.statusLabel
                )
                .filter(Boolean)
            const allNames = [...groupNames, ...statusNames]
            return allNames.length > 0 ? allNames.join(', ') : null
        },
        getClearFields: () => ({
            processStatusIds: [],
            processStatusGroupIds: [],
        }),
    },
]

export const RosterTableFooterSlot: FC = () => {
    const apiRef = useGridApiContext()
    const { tableState, setTableState, totalItems } = useRosterSearch()
    const allColumns = apiRef.current.getAllColumns()
    const visibleColumns = apiRef.current.getVisibleColumns()
    const showCount = visibleColumns.length < allColumns.length

    return (
        <GridFooterContainer>
            <Button
                variant="text"
                size="small"
                onClick={() =>
                    apiRef.current.showPreferences(
                        GridPreferencePanelsValue.columns
                    )
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
                Columns
                {showCount &&
                    ` (${visibleColumns.length}/${allColumns.length})`}
            </Button>
            <Box flex={1} />
            <TablePagination
                component="div"
                count={totalItems}
                page={tableState.page - 1}
                rowsPerPage={tableState.limit}
                onPageChange={(_, newPage) => {
                    if (totalItems > 0) {
                        setTableState({ page: newPage + 1 })
                    }
                }}
                onRowsPerPageChange={(e) =>
                    setTableState({
                        limit: parseInt(e.target.value, 10),
                        page: 1,
                    })
                }
                rowsPerPageOptions={[25, 50, 100]}
            />
        </GridFooterContainer>
    )
}

interface ActiveChip {
    key: string
    label: string
    onDelete: () => void
}

interface RosterTableToolbarSlotProps {
    rightSlot?: ReactNode
    selectedCount?: number
}

export const RosterTableToolbarSlot: FC<RosterTableToolbarSlotProps> = ({
    rightSlot,
    selectedCount = 0,
}) => {
    const {
        tableState,
        setTableState,
        hasActiveFilters,
        clearFilters,
        totalItems,
        selectAllRosters,
        isSelectingAll,
        setSelectedRosterIds,
        allSelectableIds,
    } = useRosterSqlSearch()
    const selectableTotal = allSelectableIds?.length ?? totalItems
    const filters = tableState.filters
    const setFilters = (newFilters: RosterFilterState) =>
        setTableState({ filters: newFilters })

    const userIds = [...filters.createdByUserIds, ...filters.internalUserIds]

    const { data: companiesData } = useQuery(GetCompaniesById, {
        variables: { ids: filters.companyIds },
        skip: filters.companyIds.length === 0,
    })
    const { data: recruitersData } = useQuery(GetRecruitersByUserIds, {
        variables: { ids: userIds },
        skip: userIds.length === 0,
    })
    const { data: industriesData } = useQuery(GetIndustryOptions)
    const { data: sectorsData } = useQuery(GetSectorOptions)
    const { data: titlesData } = useQuery(GetTitleOptions)
    const { data: locationsData } = useQuery(GetOfficeLocationsById, {
        variables: { ids: filters.geoLocationIds },
        skip: filters.geoLocationIds.length === 0,
    })
    const { data: fundSizeBucketsData } = useQuery(GetFundSizeBucketOptions)
    const { data: visaStatusesData } = useQuery(GetVisaStatusOptions)
    const { data: processStatusesData } = useQuery(GetProcessStatuses)

    const lookupData: LookupData = {
        companies: companiesData?.companiesById ?? [],
        recruiters: recruitersData?.recruitersByUserId ?? [],
        industries: industriesData?.industries ?? [],
        sectors: sectorsData?.sectors ?? [],
        locations: locationsData?.officeLocationsById ?? [],
        titles: titlesData?.titles ?? [],
        fundSizeBuckets: fundSizeBucketsData?.fundSizeBuckets ?? [],
        visaStatuses: visaStatusesData?.visaStatuses ?? [],
        processStatuses: processStatusesData?.processStatuses ?? [],
    }

    const activeChips: ActiveChip[] = FILTER_CONFIGS.filter((config) =>
        config.isActive(filters)
    )
        .map((config) => {
            const displayValue = config.getDisplayValue(filters, lookupData)
            if (!displayValue) return null
            return {
                key: config.key,
                label: `${config.label}: ${displayValue}`,
                onDelete: () =>
                    setFilters({ ...filters, ...config.getClearFields() }),
            }
        })
        .filter((chip): chip is ActiveChip => chip !== null)

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={getPxAsRem(8)}
            flexWrap="wrap"
            paddingBlockStart={getPxAsRem(6)}
            paddingBlockEnd={getPxAsRem(12)}
        >
            <RosterTableToolbar />
            {activeChips.map((chip) => (
                <Typography
                    key={chip.key}
                    component="span"
                    onClick={chip.onDelete}
                    sx={minimalTagSx}
                >
                    {chip.label}
                    <Clear sx={{ fontSize: '0.625rem' }} />
                </Typography>
            ))}
            <Box flex={1} />
            {hasActiveFilters && (
                <Button
                    variant="text"
                    size="small"
                    onClick={clearFilters}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                    Clear all
                </Button>
            )}

            {selectedCount > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        pl: 1.5,
                        ml: 0.5,
                    }}
                >
                    {selectedCount < selectableTotal && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={selectAllRosters}
                            disabled={isSelectingAll}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                            }}
                        >
                            {isSelectingAll ? (
                                <>
                                    <CircularProgress
                                        size={12}
                                        sx={{ mr: 0.5 }}
                                    />
                                    Selecting...
                                </>
                            ) : (
                                `Select all ${selectableTotal.toLocaleString()} rosters`
                            )}
                        </Button>
                    )}
                    {selectedCount >= selectableTotal && (
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                            }}
                        >
                            All {selectableTotal.toLocaleString()} selected
                        </Typography>
                    )}
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setSelectedRosterIds([])}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: 'auto',
                            color: 'text.secondary',
                        }}
                    >
                        Clear ({selectedCount.toLocaleString()})
                    </Button>
                </Stack>
            )}

            {rightSlot}
        </Stack>
    )
}
